import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/api-context';
import { ERROR_CODES, isApiError } from '../api/client';
import type { RepertoireLine } from '../api/types';

export interface LineMenuState {
  line: RepertoireLine;
  x: number;
  y: number;
}

export function LineContextMenu({ menu, onRename, onClose }: {
  menu: LineMenuState;
  onRename: (line: RepertoireLine) => void;
  onClose: () => void;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus({ preventScroll: true });
    function onPointerDown(event: PointerEvent): void {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      onClose();
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`${menu.line.name || 'Unnamed line'} actions`}
      className="move-context-menu"
      style={{ left: menu.x, top: menu.y }}
    >
      <button type="button" role="menuitem" onClick={() => onRename(menu.line)}>Rename line</button>
    </div>
  );
}

/** Rename metadata in place, retaining the line ID and every saved path. */
export function RenameLineDialog({ line, repertoireId, onClose, onRenamed }: {
  line: RepertoireLine;
  repertoireId: string;
  onClose: () => void;
  onRenamed?: (name: string) => void;
}): JSX.Element {
  const api = useApi();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(line.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !busy) onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onClose]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const nextName = name.trim();
    if (!nextName || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { etag } = await api.getRepertoire(repertoireId);
      if (!etag) throw new Error('Could not load the current repertoire version. Refresh and try again.');
      await api.renameLine(repertoireId, line.lineId, nextName, etag);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['lines', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoire', repertoireId] }),
        queryClient.invalidateQueries({ queryKey: ['repertoires'] }),
      ]);
      onRenamed?.(nextName);
      onClose();
    } catch (cause) {
      setError(isApiError(cause, ERROR_CODES.VERSION_MISMATCH)
        ? 'This repertoire changed while you were renaming. Try again.'
        : cause instanceof Error ? cause.message : 'Could not rename this line.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <div role="dialog" aria-modal="true" aria-labelledby="rename-line-title" className="modal">
        <h2 id="rename-line-title">Rename line</h2>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="line-name">Line name</label>
          <input
            ref={inputRef}
            id="line-name"
            value={name}
            maxLength={160}
            onChange={(event) => setName(event.target.value)}
          />
          {error && <p role="alert" className="error">{error}</p>}
          <div className="add-move-actions">
            <button type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Saving…' : 'Save name'}
            </button>
            <button type="button" className="secondary" disabled={busy} onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
